/**
 * QR Code Video Overlay
 * Full-screen video playback when accessed via ?qr_code_scan=true
 * Based on native video APIs without dependency on Cylindo modules
 */

(function () {
  'use strict';

  // Check for QR code scan parameter
  const urlParams = new URLSearchParams(window.location.search);
  const isQRScan =
    urlParams.get('qr_code_scan') === 'true' ||
    urlParams.get('qr_code_scan') === '1' ||
    urlParams.get('is_qr_code') === 'true' ||
    urlParams.get('is_qr_code') === '1';

  if (!isQRScan) {
    return;
  }

  // Wait for video data to be available
  if (!window.QRVideoData || !window.QRVideoData.firstVideo) {
    return;
  }

  const settings = window.QRVideoData.settings || {};

  // Check if QR code modal is enabled
  if (settings.enabled === false) {
    return;
  }

  const videoData = window.QRVideoData.firstVideo;
  const delayAfterEnd = settings.delayAfterEnd || 0;
  const shouldMute = settings.muteVideo === true;

  // Get overlay elements
  const overlay = document.getElementById('qr-video-overlay');
  const contentContainer = document.getElementById('qr-video-content');

  if (!overlay || !contentContainer) {
    return;
  }

  let vimeoPlayer = null;
  let isListening = false;
  let hasStartedPlaying = false;
  let playButtonShown = false;
  let currentVideoElement = null; // Store reference to current video/iframe
  let countdownInterval = null; // Store countdown interval for cancellation
  let userHasInteractedWithUnmute = false; // Track if user has already interacted with unmute
  let videoHasEnded = false; // Track if video has ended to prevent countdown cancellation

  /**
   * Wait for RedChirp iframe to be available and then lower its z-index
   */
  function lowerRedChirpZIndex() {
    const checkForRedChirp = () => {
      const redchirpIframe = document.getElementById('redchirp-iframe');
      if (redchirpIframe) {
        redchirpIframe.classList.add('qr-modal-active');
        return true;
      }
      return false;
    };

    // Try immediately first
    if (checkForRedChirp()) {
      return;
    }

    // If not found, wait for it with a timeout
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait (50 * 100ms)

    const interval = setInterval(() => {
      attempts++;
      if (checkForRedChirp()) {
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 100);
  }

  /**
   * Restore RedChirp iframe z-index to original value
   */
  function restoreRedChirpZIndex() {
    const redchirpIframe = document.getElementById('redchirp-iframe');
    if (redchirpIframe) {
      redchirpIframe.classList.remove('qr-modal-active');
    } else {
    }
  }

  // Show overlay
  overlay.style.display = 'flex';

  // Lower RedChirp iframe z-index to appear below QR modal
  lowerRedChirpZIndex();

  // Prevent page scrolling when modal is open
  document.body.style.overflow = 'hidden';

  // Add close button if enabled
  if (settings.showCloseButton) {
    const closeButton = document.createElement('button');
    closeButton.className = 'close-button';
    closeButton.textContent = 'Close (X)';
    closeButton.setAttribute('aria-label', 'Close video overlay');
    closeButton.setAttribute('type', 'button');
    closeButton.addEventListener('click', function () {
      closeOverlay();
    });
    overlay.appendChild(closeButton);
  }

  // Focus trap: keep Tab within overlay; order: Close -> Unmute -> loop
  function handleTabTrap(event) {
    if (event.key !== 'Tab') return;
    const focusables = Array.prototype.slice
      .call(overlay.querySelectorAll('.close-button, .unmute-button'))
      .filter(function (el) {
        return el && !el.disabled && el.offsetParent !== null;
      });
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      // Shift+Tab on first sends focus to last
      if (active === first || !overlay.contains(active)) {
        last.focus();
        event.preventDefault();
      }
    } else {
      // Tab on last loops to first; if focus is outside, go to first
      if (active === last || !overlay.contains(active)) {
        first.focus();
        event.preventDefault();
      }
    }
  }

  // Add ESC key listener
  function handleEscKey(event) {
    if (event.key === 'Escape' || event.keyCode === 27) {
      closeOverlay();
    }
  }
  document.addEventListener('keydown', handleEscKey);
  document.addEventListener('keydown', handleTabTrap, true);

  /**
   * Close overlay and cleanup
   */
  function closeOverlay() {
    // Cancel any active countdown
    cancelCountdown();

    // Restore RedChirp iframe z-index to original value
    restoreRedChirpZIndex();

    // Remove key listeners
    document.removeEventListener('keydown', handleEscKey);
    document.removeEventListener('keydown', handleTabTrap, true);

    // Restore page scrolling
    document.body.style.overflow = '';

    // Reset focus to top of page for "skip to content" accessibility
    // First blur any active element
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
    // Focus the body or first focusable element
    document.body.setAttribute('tabindex', '-1');
    document.body.focus();
    document.body.removeAttribute('tabindex');

    // Clean up Vimeo player
    if (vimeoPlayer) {
      vimeoPlayer.destroy().catch(function (error) {
        console.error('[QR Video] Error destroying Vimeo player:', error);
      });
      vimeoPlayer = null;
    }

    overlay.style.display = 'none';
    contentContainer.innerHTML = '';
  }

  /**
   * Handle video end
   */
  function handleVideoEnd() {
    videoHasEnded = true; // Set flag to prevent countdown cancellation from post-end events
    if (delayAfterEnd > 0) {
      showCloseCountdown(delayAfterEnd);
    } else {
      closeOverlay();
    }
  }

  /**
   * Cancel any active countdown
   */
  function cancelCountdown() {
    if (countdownInterval) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }

    // Remove countdown element if it exists
    const countdown = overlay.querySelector('.close-countdown');
    if (countdown) {
      countdown.remove();
    }
  }

  /**
   * Show countdown before closing
   */
  function showCloseCountdown(seconds) {
    // Cancel any existing countdown first
    cancelCountdown();

    const countdown = document.createElement('div');
    countdown.className = 'close-countdown';
    countdown.textContent = `Closing in ${seconds} seconds...`;
    overlay.appendChild(countdown);

    let remaining = seconds;
    countdownInterval = setInterval(function () {
      remaining--;
      if (remaining > 0) {
        countdown.textContent = `Closing in ${remaining} seconds...`;
      } else {
        clearInterval(countdownInterval);
        countdownInterval = null;
        closeOverlay();
      }
    }, 1000);
  }

  /**
   * Show play button fallback
   */
  function showPlayButton(playFn) {
    if (playButtonShown) return;

    const playBtn = document.createElement('div');
    playBtn.className = 'play-button';
    playBtn.innerHTML = '▶️ Tap to Play Video';
    playBtn.addEventListener('click', function () {
      playFn();
      playBtn.remove();
      playButtonShown = true;
    });
    overlay.appendChild(playBtn);
    playButtonShown = true;
  }

  /**
   * Create and add unmute button to overlay
   */
  function createUnmuteButton() {
    // Check if button already exists
    if (overlay.querySelector('.unmute-button')) {
      return;
    }

    const unmuteBtn = document.createElement('button');
    unmuteBtn.className = 'unmute-button';
    unmuteBtn.setAttribute('type', 'button');
    unmuteBtn.setAttribute('aria-label', 'Unmute video');
    unmuteBtn.innerHTML = `
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
      </svg>
      Tap to Unmute
    `;

    unmuteBtn.addEventListener('click', function () {
      handleUnmute();
      unmuteBtn.remove();
    });

    overlay.appendChild(unmuteBtn);
  }

  /**
   * Hide unmute button if it exists
   */
  function hideUnmuteButton() {
    const unmuteBtn = overlay.querySelector('.unmute-button');
    if (unmuteBtn) {
      unmuteBtn.remove();
    }
  }

  /**
   * Show unmute button overlay
   */
  function showUnmuteButton() {
    try {
      // Don't show unmute button if user has already interacted with it
      if (userHasInteractedWithUnmute) {
        return;
      }

      // Only show unmute button if video is actually muted
      let shouldShowUnmute = false;

      // Check if video is actually muted
      if (currentVideoElement) {
        if (videoData.type === 'native' && currentVideoElement.muted) {
          shouldShowUnmute = true;
        } else if (videoData.type === 'vimeo' && vimeoPlayer) {
          // For Vimeo, check both the user setting and actual volume
          if (shouldMute) {
            shouldShowUnmute = true;
          } else {
            // If user wants unmuted, check actual volume asynchronously
            vimeoPlayer
              .getVolume()
              .then(function (volume) {
                if (volume === 0) {
                  // Video is actually muted, show unmute button
                  if (!overlay.querySelector('.unmute-button')) {
                    createUnmuteButton();
                  }
                }
              })
              .catch(function (error) {
                console.error('[QR Video] Could not get Vimeo volume:', error);
                // If we can't get volume and user wants unmuted, don't show button
              });
            // Don't set shouldShowUnmute here since we're handling it asynchronously
            return;
          }
        }
      }

      if (!shouldShowUnmute) {
        return;
      }

      createUnmuteButton();
    } catch (error) {
      console.error('[QR Video] Error showing unmute button:', error);
    }
  }

  /**
   * Handle unmute based on video provider
   */
  function handleUnmute() {
    if (videoData.type === 'native') {
      // Native Shopify video
      const video = contentContainer.querySelector('video');
      if (video) {
        video.muted = false;
        video.volume = 1;
      }
    } else if (videoData.type === 'vimeo') {
      // Vimeo video
      if (vimeoPlayer) {
        vimeoPlayer
          .setVolume(1)
          .then(function () {
            // Do something here
          })
          .catch(function (error) {
            console.error('[QR Video] Failed to unmute Vimeo:', error);
          });
      }
    } else if (videoData.type === 'youtube') {
      // YouTube video
      const iframe = contentContainer.querySelector('iframe');
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'unMute' }), '*');
      }
    }

    // Hide the unmute button after unmuting
    hideUnmuteButton();

    // Mark that user has interacted with unmute
    userHasInteractedWithUnmute = true;
  }

  /**
   * Setup native video (Shopify-hosted)
   */
  function setupNativeVideo() {
    const video = document.createElement('video');
    video.setAttribute('playsinline', '');
    video.setAttribute('preload', 'auto');
    // Set muted attribute based on user preference for autoplay success
    if (shouldMute) {
      video.setAttribute('muted', '');
    }
    if (settings.showControls) {
      video.setAttribute('controls', '');
    }
    if (videoData.poster) {
      video.setAttribute('poster', videoData.poster);
    }

    // Add sources
    videoData.sources.forEach(function (source) {
      const sourceEl = document.createElement('source');
      sourceEl.src = source.url;
      sourceEl.type = source.mimeType || 'video/mp4';
      video.appendChild(sourceEl);
    });

    // Event listeners
    video.addEventListener('ended', handleVideoEnd);
    video.addEventListener('play', function () {
      hasStartedPlaying = true;
      videoHasEnded = false; // Reset flag when user restarts playback
      const playBtn = overlay.querySelector('.play-button');
      if (playBtn) playBtn.remove();
      // Show unmute button if video is muted
      showUnmuteButton();
      // Cancel countdown if user restarts playback
      cancelCountdown();
    });
    video.addEventListener('error', function () {
      console.error('[QR Video] Video playback error');
      closeOverlay();
    });

    // Cancel countdown on user interactions
    video.addEventListener('seeked', function () {
      // Cancel countdown if user seeks (but not if video has ended)
      if (!videoHasEnded) {
        cancelCountdown();
      }
    });
    video.addEventListener('pause', function () {
      // Cancel countdown if user pauses (but not if video has ended)
      if (!videoHasEnded) {
        cancelCountdown();
      }
    });
    video.addEventListener('timeupdate', function () {
      // Only cancel if user is actively seeking (not just normal playback)
      // Don't cancel if video has ended
      if (video.seeking && !video.ended) {
        cancelCountdown();
      }
    });

    // Hide unmute button if user manually unmutes video
    video.addEventListener('volumechange', function () {
      if (!video.muted) {
        hideUnmuteButton();
        userHasInteractedWithUnmute = true;
      }
    });

    contentContainer.appendChild(video);
    currentVideoElement = video;

    // Try autoplay with the desired mute state
    if (shouldMute) {
      // User wants muted, so start muted for better autoplay success
      video.muted = true;

      video
        .play()
        .then(function () {
          // Do something here
        })
        .catch(function (error) {
          // Show play button fallback
          showPlayButton(function () {
            video.play().catch(function (e) {
              console.error('[QR Video] Manual play failed:', e);
            });
          });
        });
    } else {
      // User wants unmuted, try unmuted first, then fall back to muted if needed
      video.muted = false;

      video
        .play()
        .then(function () {})
        .catch(function (error) {
          // Fall back to muted autoplay
          video.muted = true;
          video
            .play()
            .then(function () {
              // Show unmute button since user wanted unmuted but we had to use muted
              showUnmuteButton();
            })
            .catch(function (mutedError) {
              // Show play button fallback
              showPlayButton(function () {
                video.play().catch(function (e) {
                  console.error('[QR Video] Manual play failed:', e);
                });
              });
            });
        });
    }

    // Check for HLS
    const hasHLS = videoData.sources.some(function (s) {
      return s.mimeType && s.mimeType.indexOf('mpegurl') !== -1;
    });

    if (hasHLS && !video.canPlayType('application/vnd.apple.mpegurl') && window.Hls) {
      const hls = new window.Hls();
      const hlsSource = videoData.sources.find(function (s) {
        return s.mimeType && s.mimeType.indexOf('mpegurl') !== -1;
      });
      if (hlsSource) {
        hls.loadSource(hlsSource.url);
        hls.attachMedia(video);
        hls.on(window.Hls.Events.MANIFEST_PARSED, function () {
          // Try autoplay with the desired mute state
          if (shouldMute) {
            video.muted = true;

            video
              .play()
              .then(function () {
                // Do something here
              })
              .catch(function (error) {
                // Show play button fallback
                showPlayButton(function () {
                  video.play().catch(function (e) {
                    console.error('[QR Video] Manual HLS play failed:', e);
                  });
                });
              });
          } else {
            // User wants unmuted, try unmuted first, then fall back to muted if needed
            video.muted = false;

            video
              .play()
              .then(function () {})
              .catch(function (error) {
                // Fall back to muted autoplay
                video.muted = true;
                video
                  .play()
                  .then(function () {
                    // Show unmute button since user wanted unmuted but we had to use muted
                    showUnmuteButton();
                  })
                  .catch(function (mutedError) {
                    // Show play button fallback
                    showPlayButton(function () {
                      video.play().catch(function (e) {
                        console.error('[QR Video] Manual HLS play failed:', e);
                      });
                    });
                  });
              });
          }
        });
      }
    }
  }

  /**
   * Setup YouTube video
   */
  function setupYouTubeVideo() {
    const iframe = document.createElement('iframe');
    iframe.src = videoData.embedUrl;
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('allow', 'autoplay; fullscreen');
    contentContainer.appendChild(iframe);
    currentVideoElement = iframe;

    // YouTube postMessage control
    function controlPlayer(command) {
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: command }), '*');
      }
    }

    // Start listening for state changes
    iframe.addEventListener('load', function () {
      // Start listening
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), '*');
        isListening = true;
      }

      // Try to play after delay
      setTimeout(function () {
        controlPlayer('playVideo');
      }, 1000);
    });

    // Listen for YouTube messages
    window.addEventListener('message', function (event) {
      if (event.origin === 'https://www.youtube.com') {
        try {
          const data = JSON.parse(event.data);
          if (data.event === 'infoDelivery' && data.info && data.info.playerState !== undefined) {
            const state = data.info.playerState;
            // 0=ended, 1=playing
            if (state === 0) {
              handleVideoEnd();
            } else if (state === 1) {
              hasStartedPlaying = true;
              const playBtn = overlay.querySelector('.play-button');
              if (playBtn) playBtn.remove();
              // Show unmute button if video is muted
              showUnmuteButton();
            }
          }
        } catch (e) {
          // Ignore non-JSON messages
        }
      }
    });

    // Show play button fallback after 5 seconds
    setTimeout(function () {
      if (!hasStartedPlaying) {
        showPlayButton(function () {
          controlPlayer('playVideo');
        });
      }
    }, 5000);
  }

  /**
   * Setup Vimeo video
   */
  function setupVimeoVideo() {
    if (!window.Vimeo) {
      console.error('[QR Video] Vimeo SDK not loaded');
      closeOverlay();
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.src = videoData.embedUrl;
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('allow', 'autoplay; fullscreen');
    contentContainer.appendChild(iframe);
    currentVideoElement = iframe;

    // Wait for iframe to load, then initialize Vimeo Player SDK (per working prototype)
    iframe.addEventListener('load', function () {
      try {
        // Initialize Vimeo Player SDK
        vimeoPlayer = new window.Vimeo.Player(iframe);

        // Set up event listeners
        vimeoPlayer.on('ended', function () {
          // Only close if video actually started playing
          if (hasStartedPlaying) {
            handleVideoEnd();
          } else {
          }
        });

        vimeoPlayer.on('play', function () {
          hasStartedPlaying = true;
          videoHasEnded = false; // Reset flag when user restarts playback
          const playBtn = overlay.querySelector('.play-button');
          if (playBtn) playBtn.remove();
          // Show unmute button if video is muted
          showUnmuteButton();
          // Cancel countdown if user restarts playback
          cancelCountdown();
        });

        vimeoPlayer.on('pause', function () {
          // Cancel countdown if user pauses video (but not if video has ended)
          if (!videoHasEnded) {
            cancelCountdown();
          }
        });

        vimeoPlayer.on('error', function (error) {
          console.error('[QR Video] Vimeo player error:', error);
          closeOverlay();
        });

        // Cancel countdown on Vimeo user interactions
        vimeoPlayer.on('seeked', function () {
          // Cancel countdown if user seeks (but not if video has ended)
          if (!videoHasEnded) {
            cancelCountdown();
          }
        });
        vimeoPlayer.on('timeupdate', function () {
          // Cancel countdown if user is actively seeking
          if (vimeoPlayer.getCurrentTime && vimeoPlayer.getCurrentTime() !== vimeoPlayer.getDuration()) {
            // This is a simple check - in practice, seeking detection is complex
            // The 'seeked' event above is more reliable for this
          }
        });

        // Hide unmute button if Vimeo video volume changes to unmuted
        vimeoPlayer.on('volumechange', function (data) {
          if (data.volume > 0) {
            hideUnmuteButton();
            userHasInteractedWithUnmute = true;
          }
          // Don't show button again if volume goes to 0 - user might be seeking
        });

        // Wait a bit for the player to fully initialize, then try to play
        setTimeout(function () {
          // Check if player is ready
          vimeoPlayer
            .ready()
            .then(function () {
              // Set volume based on mute setting
              if (shouldMute) {
                return vimeoPlayer.setVolume(0);
              } else {
                return vimeoPlayer.setVolume(1);
              }
            })
            .then(function () {
              return vimeoPlayer.play();
            })
            .then(function () {
              // Keep controls visible by periodically interacting with the player
              // This prevents Vimeo from auto-hiding controls
              setInterval(function () {
                vimeoPlayer.getVolume().catch(function () {
                  // Ignore errors, just need to keep the connection alive
                });
              }, 2000); // Ping every 2 seconds
            })
            .catch(function (error) {
              // Vimeo has good autoplay support, so we don't show play button
              // The video should still work with the iframe autoplay parameter
            });
        }, 2000); // Wait 2 seconds for full initialization
      } catch (e) {
        console.error('[QR Video] Error creating Vimeo Player:', e);
      }
    });
  }

  // Initialize based on video type
  if (videoData.type === 'native') {
    setupNativeVideo();
  } else if (videoData.type === 'youtube') {
    setupYouTubeVideo();
  } else if (videoData.type === 'vimeo') {
    setupVimeoVideo();
  } else {
    // Do something else here
    // closeOverlay();
  }

  // Click overlay to close
  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) {
      closeOverlay();
    }
  });

  // Fallback timeout
  setTimeout(function () {
    if (overlay.style.display !== 'none') {
      closeOverlay();
    }
  }, 300000); // 5 minutes max
})();
